/** Shared by Itinerary review and server notifications; no browser dependencies. */
import { assignedTravellers, conflictRecipients } from './travellerRoutePolicy.mjs';
type Party = { participantIds?: string[]; contributedBy?: string; addedBy?: string };
export type ConflictStay = Party & { id?: string; name?: string; checkIn: string; checkOut: string };
export type ConflictLeg = Party & { id?: string; isRental?: boolean; mode: string; dep: string; arr: string;
  depTz?: string; arrTz?: string; from: string; to: string;
  fromCode?: string; toCode?: string; fromCity?: string; toCity?: string };

export function conflictAudience(a: Party, b: Party, members: string[], managers: (string | undefined)[]): string[] {
  const party = (item: Party) => ({ participantIds: assignedTravellers(item.participantIds, members),
    ownerId: item.contributedBy || item.addedBy, adminIds: managers.filter((id): id is string => !!id) });
  return conflictRecipients(party(a), party(b), members);
}

/** Legacy stays can be copied across days; a copy is not another booking. */
export function uniqueConflictStays<S extends ConflictStay>(stays: S[]): S[] {
  const seen = new Set<string>();
  return stays.filter(s => {
    const key = s.id || JSON.stringify([s.name, s.checkIn, s.checkOut, s.participantIds]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function sharesTraveller(a: Party, b: Party, eligible: string[]): boolean {
  const fixed = [a.participantIds, b.participantIds].filter(Boolean) as string[][];
  const current = (ids: string[]) => !eligible.length || ids.some(id => eligible.includes(id));
  if (fixed.length < 2) return !fixed.length || current(fixed[0]);
  return fixed[0].some(id => fixed[1].includes(id) && current([id]));
}

/** Potential duplicate, not a decision to delete: travellers may have separate tickets. */
export function sameTransportLeg(a: ConflictLeg, b: ConflictLeg): boolean {
  const endpoint = (code: string | undefined, city: string | undefined, place: string) => (code || city || place || '').trim().toLowerCase();
  return a !== b && !a.isRental && !b.isRental && !(a.id && a.id === b.id) && a.mode === b.mode
    && !!a.dep?.includes('T') && a.dep === b.dep && a.arr === b.arr
    && !!(a.fromCode || a.fromCity || a.from) && !!(a.toCode || a.toCity || a.to)
    && endpoint(a.fromCode && b.fromCode ? a.fromCode : '', a.fromCity, a.from) === endpoint(a.fromCode && b.fromCode ? b.fromCode : '', b.fromCity, b.from)
    && endpoint(a.toCode && b.toCode ? a.toCode : '', a.toCity, a.to) === endpoint(a.toCode && b.toCode ? b.toCode : '', b.toCity, b.to);
}

/** UTC instant of an offset-free local wall clock in an IANA timezone. */
export function localToUTCMs(localStr: string, tz: string): number {
  const [datePart, timePart = '00:00'] = localStr.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  const localAsUTC = Date.UTC(y, mo - 1, d, h, mi);
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
    const getOffset = (utcMs: number): number => {
      const parts = fmt.formatToParts(new Date(utcMs));
      const pv = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0');
      return Date.UTC(pv('year'), pv('month') - 1, pv('day'), pv('hour'), pv('minute')) - utcMs;
    };
    // Converge using the offset in effect at each candidate (including DST).
    let utcMs = localAsUTC;
    for (let i = 0; i < 4; i++) utcMs = localAsUTC - getOffset(utcMs);
    return utcMs;
  } catch {
    return localAsUTC;
  }
}

export function bookingConflicts<S extends ConflictStay, T extends ConflictLeg>(stays: S[], legs: T[], eligible: string[]) {
  const out: { kind: 'stay' | 'journey'; a: S | T; b: S | T; key: string }[] = [];
  const key = (kind: string, a: { id?: string }, b: { id?: string }) =>
    `conflict-${kind}-${[a.id, b.id].map(id => String(id ?? '').replace(/[^\w-]/g, '_')).sort().join('-')}`.slice(0, 200);
  const night = (v: string) => v.slice(0, 10);
  stays.forEach((a, i) => stays.slice(i + 1).forEach(b => {
    if (night(a.checkIn) < night(b.checkOut) && night(b.checkIn) < night(a.checkOut) && sharesTraveller(a, b, eligible))
      out.push({ kind: 'stay', a, b, key: key('stay', a, b) });
  }));
  const timed = legs.filter((t, i, all) => !t.isRental && t.depTz && t.arrTz && t.dep?.includes('T') && t.arr?.includes('T')
    && (!t.id || all.findIndex(other => other.id === t.id) === i));
  const span = (t: T) => [localToUTCMs(t.dep, t.depTz!), localToUTCMs(t.arr, t.arrTz!)];
  const times = new Map(timed.map(t => [t, span(t)]));
  timed.forEach((a, i) => timed.slice(i + 1).forEach(b => {
    if (!(a.id && a.id === b.id) && !sameTransportLeg(a, b) && times.get(a)![0] < times.get(b)![1] && times.get(b)![0] < times.get(a)![1] && sharesTraveller(a, b, eligible))
      out.push({ kind: 'journey', a, b, key: key('journey', a, b) });
  }));
  return out;
}
